import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  ShieldCheck,
  ShieldAlert,
  Clock,
  Loader2,
  ExternalLink,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const RANGES = [7, 30, 90];

const STAGE_META = {
  pending: {
    label: "Pending invite",
    cls: "border-slate-200 bg-slate-50 text-slate-700",
    Icon: Clock,
  },
  in_progress: {
    label: "In progress",
    cls: "border-blue-200 bg-blue-50 text-blue-700",
    Icon: Loader2,
  },
  clear: {
    label: "Clear",
    cls: "border-emerald-200 bg-emerald-50 text-emerald-700",
    Icon: ShieldCheck,
  },
  consider: {
    label: "Consider",
    cls: "border-amber-200 bg-amber-50 text-amber-800",
    Icon: ShieldAlert,
  },
};

export default function BackgroundCheckPipelineWidget() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [platformEnabled, setPlatformEnabled] = useState(true);
  const [drilldown, setDrilldown] = useState(null); // bucket key

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await api.get("/background-checks/status");
        if (cancelled) return;
        setPlatformEnabled(!!s.data?.platform_enabled);
      } catch {
        if (!cancelled) setPlatformEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/dashboard/bg-pipeline", {
          params: { days },
        });
        if (!cancelled) setData(data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [days]);

  if (!platformEnabled) return null;

  const counts = data?.counts || {};
  const buckets = data?.buckets || {};

  return (
    <div
      data-testid="bg-pipeline-widget"
      className="mt-6 border border-slate-200 bg-white"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50 px-5 py-3">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          <ShieldCheck className="h-3 w-3" />
          Background check pipeline
        </div>
        <div className="flex border border-slate-200">
          {RANGES.map((r) => (
            <button
              key={r}
              data-testid={`bg-pipeline-range-${r}`}
              onClick={() => setDays(r)}
              className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${
                days === r
                  ? "bg-slate-900 text-white"
                  : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              {r}d
            </button>
          ))}
        </div>
      </div>
      <div className="px-5 py-4">
        {loading ? (
          <div className="flex h-24 items-center justify-center text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : counts.total === 0 ? (
          <div
            data-testid="bg-pipeline-empty"
            className="py-4 text-center text-sm text-slate-400"
          >
            No background checks ordered in the last {days} days.
          </div>
        ) : (
          <div className="grid gap-px bg-slate-100 sm:grid-cols-4">
            {["pending", "in_progress", "clear", "consider"].map((key) => {
              const meta = STAGE_META[key];
              const count = counts[key] || 0;
              const Icon = meta.Icon;
              return (
                <button
                  key={key}
                  type="button"
                  data-testid={`bg-pipeline-tile-${key}`}
                  onClick={() => count > 0 && setDrilldown(key)}
                  disabled={count === 0}
                  className={`flex flex-col items-start gap-1 bg-white px-4 py-3 text-left transition ${
                    count > 0
                      ? "cursor-pointer hover:bg-slate-50"
                      : "cursor-default opacity-60"
                  }`}
                >
                  <div
                    className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${meta.cls}`}
                  >
                    <Icon className="h-3 w-3" />
                    {meta.label}
                  </div>
                  <div className="text-2xl font-extrabold tabular-nums text-slate-900">
                    {count}
                  </div>
                  {count > 0 && (
                    <div className="text-[10px] font-bold uppercase tracking-wider text-blue-700">
                      View candidates →
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <Dialog
        open={!!drilldown}
        onOpenChange={(o) => !o && setDrilldown(null)}
      >
        <DialogContent
          data-testid="bg-pipeline-drilldown"
          className="max-w-2xl border border-slate-200"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              {drilldown && STAGE_META[drilldown] && (() => {
                const DrillIcon = STAGE_META[drilldown].Icon;
                return (
                  <>
                    <DrillIcon className="h-4 w-4" />
                    {STAGE_META[drilldown].label} — last {days} days
                  </>
                );
              })()}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[460px] divide-y divide-slate-100 overflow-y-auto">
            {drilldown &&
              (buckets[drilldown] || []).map((bg) => (
                <div
                  key={bg.id}
                  data-testid={`bg-pipeline-row-${bg.id}`}
                  className="flex items-center justify-between gap-3 py-3"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-900">
                      {bg.candidate_name || "—"}
                    </div>
                    <div className="truncate text-[11px] text-slate-500">
                      {bg.candidate_email}
                    </div>
                    <div className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-400">
                      {bg.package_label}
                      {bg.adverse_action_state &&
                        bg.adverse_action_state !== "none" && (
                          <>
                            {" · "}
                            <span className="text-amber-700">
                              {bg.adverse_action_state.replace(/_/g, " ")}
                            </span>
                          </>
                        )}
                    </div>
                  </div>
                  {bg.job_id && (
                    <Link
                      to={`/applicants`}
                      className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-700 hover:underline"
                    >
                      Open
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  )}
                </div>
              ))}
            {drilldown && (buckets[drilldown] || []).length === 0 && (
              <div className="py-6 text-center text-sm text-slate-400">
                None.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
